import { InternalCommentOverview, InternalCommentStatusEnum } from '../../../../types/internal-comment';
import { useCommentsAddon } from './useCommentsAddon';
import { useRef, useState } from 'react';
import { BadgeV3, ButtonV2, Dropdown, Spinner } from '@cimplico/components';
import { useUpdateInternalComments } from '../../../../queries/use-update-internal-comments';
import { useDeleteInternalComment } from '../../../../queries/use-delete-internal-comment';
import { useInternalComment } from '../../../../queries/use-internal-comment';
import { useDeleteInternalCommentReply } from '../../../../queries/use-delete-internal-comment-reply';
import { useUpdateInternalCommentReply } from '../../../../queries/use-update-internal-comment-reply';
import { UpdateReplyProps } from '../../../../components/structured-text-card/Reply';
import { useCreateInternalCommentReply } from '../../../../queries/use-create-internal-comment-reply';
import { CreateReplyProps } from '../../../../components/structured-text-card/NewReply';
import { EditorRef } from '../../../../components/rich-text-editor/Editor';
import StructuredTextCard from '../../../../components/structured-text-card/StructuredTextCard';

type CommentProps = {
    comment: InternalCommentOverview;
    disableAction: boolean;
};

function Comment({ comment, disableAction }: CommentProps): JSX.Element {
    const ref = useRef<EditorRef>(null);

    const { editingComment, setEditingComment } = useCommentsAddon();

    const [editingReply, setEditingReply] = useState<string | undefined>(undefined);
    const [replyEditorFocused, setReplyEditorFocused] = useState(false);
    const [reply, setReply] = useState({ rawContent: '', structuredContent: '' });

    const isEditing = editingComment === comment.id;
    const isActive = comment.status !== InternalCommentStatusEnum.RESOLVED;

    const commentQuery = useInternalComment(comment.workpaperId, comment.id);
    const updateCommentQuery = useUpdateInternalComments(comment.workpaperId);
    const deleteCommentQuery = useDeleteInternalComment(comment.workpaperId);
    const createReplyQuery = useCreateInternalCommentReply(comment.workpaperId, comment.id);
    const deleteReplyQuery = useDeleteInternalCommentReply(comment.workpaperId, comment.id);
    const updateReplyQuery = useUpdateInternalCommentReply(comment.workpaperId, comment.id);

    const replies = commentQuery.data?.internalComment?.replies ?? [];

    function updateComment(editingText: { rawContent: string; structuredContent?: { [key: string]: unknown } }): void {
        if (!editingText.rawContent) {
            return;
        }

        const body = {
            id: comment.id,
            fields: { rawComment: editingText.rawContent, structuredComment: editingText.structuredContent },
        };

        updateCommentQuery.mutate([body], {
            onSuccess: () => {
                setEditingComment('');
            },
        });
    }

    function resolveComment(): void {
        updateCommentQuery.mutate([{ id: comment.id, fields: { status: InternalCommentStatusEnum.RESOLVED } }]);
    }

    function reopenComment(): void {
        updateCommentQuery.mutate([{ id: comment.id, fields: { status: InternalCommentStatusEnum.ACTIVE } }]);
    }

    function deleteComment(): void {
        deleteCommentQuery.mutate({ id: comment.id });
    }

    function createReply({ params, resolve = false }: { params: CreateReplyProps; resolve?: boolean }): void {
        createReplyQuery.mutate(
            { reply: { rawReply: params.rawContent, structuredReply: JSON.parse(params.structuredContent) } },
            {
                onSuccess: () => {
                    setReply({ rawContent: '', structuredContent: '' });
                    ref.current?.clear();
                    if (resolve) {
                        resolveComment();
                    }
                    setReplyEditorFocused(false);
                },
            }
        );
    }

    function updateReply({ params, resolve = false }: { params: UpdateReplyProps; resolve?: boolean }): void {
        if (!params.rawContent) {
            return;
        }

        const body = { id: params.id, rawReply: params.rawContent, structuredReply: params.structuredContent || {} };

        updateReplyQuery.mutate(body, {
            onSuccess: () => {
                setEditingReply(undefined);
                if (resolve) {
                    resolveComment();
                }
            },
        });
    }

    function deleteReply(id: string): void {
        deleteReplyQuery.mutate({ id });
    }

    return (
        <StructuredTextCard>
            <StructuredTextCard.Author createdAt={comment.createdAt} createdBy={comment.createdBy} />
            {!isEditing && (
                <Dropdown dotsDirection="horizontal" disabled={disableAction}>
                    {!isActive && <Dropdown.Option label="Re-open" onClick={reopenComment} value="reopen" />}
                    {isActive && <Dropdown.Option label="Resolve" onClick={resolveComment} value="resolve" />}
                    {isActive && <Dropdown.Option label="Edit" onClick={(): void => setEditingComment(comment.id)} value="edit" />}
                    {isActive && <Dropdown.Option label="Delete" onClick={deleteComment} value="delete" />}
                </Dropdown>
            )}
            <StructuredTextCard.Message
                isEditing={isEditing}
                isSaving={updateCommentQuery.isLoading}
                rawContent={comment.rawComment}
                structuredContent={comment.structuredComment || undefined}
                onCancel={(): void => setEditingComment('')}
                onUpdate={updateComment}
            />
            {!isEditing && (
                <StructuredTextCard.QuickActions>
                    {comment.status === InternalCommentStatusEnum.ACTIVE && !disableAction && (
                        <ButtonV2
                            label="Resolve"
                            type="hollow"
                            size="sm"
                            isLoading={updateCommentQuery.isLoading}
                            isDisabled={updateCommentQuery.isLoading}
                            onClick={resolveComment}
                        />
                    )}
                    {comment.status === InternalCommentStatusEnum.RESOLVED && <BadgeV3 label="Resolved" color="green" rounded />}
                </StructuredTextCard.QuickActions>
            )}
            <StructuredTextCard.RepliesList
                ref={ref}
                reply={reply}
                isCreating={createReplyQuery.isLoading}
                isEditorFocused={replyEditorFocused}
                canCreate={comment.status === InternalCommentStatusEnum.ACTIVE && !disableAction}
                extraActions={[{ label: 'Save & Resolve', onClick: (params): void => createReply({ params, resolve: true }) }]}
                onNewReply={setReply}
                onCreate={(params): void => createReply({ params })}
                onEditorFocused={setReplyEditorFocused}
            >
                {replies.map((reply) => {
                    return (
                        <StructuredTextCard.Reply
                            key={reply.id}
                            reply={reply}
                            isEditing={editingReply === reply.id}
                            isSaving={updateReplyQuery.isLoading || deleteReplyQuery.isLoading}
                            canEdit={comment.status === InternalCommentStatusEnum.ACTIVE}
                            extraActions={[{ label: 'Save & Resolve', onClick: (params): void => updateReply({ params, resolve: true }) }]}
                            onEditing={(isEditing): void => setEditingReply(isEditing ? reply.id : undefined)}
                            onDelete={deleteReply}
                            onUpdate={(params): void => updateReply({ params })}
                        />
                    );
                })}
            </StructuredTextCard.RepliesList>
        </StructuredTextCard>
    );
}

export default Comment;
